import { useState, useEffect } from 'react';
import { Droplets, Heart, Award, Clock, Phone, Share2, ShieldCheck, Building2, AlertTriangle } from 'lucide-react';
import { T } from '@design/index';
import { GOV_TYPE, GOV_COMPLIANCE, govPageContainer } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { PatientCertificateDoc, PatientProfile } from '../types';
import NHCIdBadge from '../components/NHCIdBadge';
import NeoVerifyBadge from '../components/NeoVerifyBadge';
import InstitutionShareModal from '../components/InstitutionShareModal';
import Spinner from '../../../components/ui/Spinner';

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function BloodBankReadiness() {
  const [profile, setProfile] = useState<PatientProfile | null>(null);
  const [bloodCerts, setBloodCerts] = useState<PatientCertificateDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareTarget, setShareTarget] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      patientApi.getProfile(),
      patientApi.getCertificates({ type: 'BLOOD_DONATION_CERTIFICATE' }),
      patientApi.getCertificates({ type: 'BLOOD_ISSUE_CERTIFICATE' }),
    ]).then(([p, donations, issues]) => {
      if (alive) {
        setProfile(p);
        setBloodCerts([...donations, ...issues]);
      }
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (!profile) return null;

  const donationCerts = bloodCerts.filter(c => c.certificateType === 'BLOOD_DONATION_CERTIFICATE');
  const issueCerts = bloodCerts.filter(c => c.certificateType === 'BLOOD_ISSUE_CERTIFICATE');

  return (
    <div className="space-y-6" style={govPageContainer()}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="flex items-center gap-2" style={GOV_TYPE.pageTitle}>
            <Droplets className="w-6 h-6 text-red-600" /> Blood Bank Readiness
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Donation history, certificates & emergency blood access</p>
        </div>
        <NHCIdBadge nhcId={profile.nhcId} />
      </div>

      {/* Blood Group Card */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-2xl p-5 text-white">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
            <Droplets className="w-8 h-8" />
          </div>
          <div>
            <p className="text-3xl font-bold">{profile.bloodGroup || 'Unknown'}</p>
            <p className="text-red-100 text-sm">Blood Group</p>
          </div>
          <div className="ml-auto text-right">
            <p className="text-lg font-bold">{donationCerts.length}</p>
            <p className="text-red-100 text-sm">Donations</p>
          </div>
        </div>
      </div>

      {/* Donor eligibility */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Heart className="w-4 h-4 text-red-500" /> Donor Eligibility
        </h3>
        {/* No screening data exists in this app — a green "eligible" verdict
            here would be a fabricated medical determination. */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2 text-sm text-amber-800">
          <ShieldCheck className="w-5 h-5 shrink-0" />
          <div>
            <p className="font-semibold">Eligibility is determined by the blood bank</p>
            <p className="text-xs mt-0.5">This app cannot assess donor eligibility — the blood bank screens every donor (haemoglobin, recent illness, medication, last donation) before donation. {donationCerts.length > 0 ? 'Your past donation certificates are listed below.' : 'No past donations are on record here.'}</p>
          </div>
        </div>
      </div>

      {/* Donation history */}
      <div>
        <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Award className="w-4 h-4 text-red-500" /> Donation Certificates
        </h2>
        {donationCerts.length === 0 ? (
          <div className="card p-6 text-center">
            <Droplets className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No donation records yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {donationCerts.map(c => (
              <div key={c.id} className="card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{c.title}</h3>
                    {c.description && <p className="text-xs text-gray-500 mt-1">{c.description}</p>}
                    <div className="flex gap-3 mt-2 text-[11px] text-gray-500">
                      <span>{fmtDate(c.issueDate)}</span>
                      {c.issuerName && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{c.issuerName}</span>}
                    </div>
                  </div>
                  <NeoVerifyBadge status={c.verificationStatus} neoVerifyId={c.neoVerifyId} />
                </div>
                <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100">
                  <button onClick={() => setShareTarget({ id: c.id, title: c.title })}
                    className="text-[11px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                    <Share2 className="w-3 h-3" /> Share with Hospital/Blood Bank
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Blood Request History */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-orange-500" /> Blood Request History
        </h3>
        {issueCerts.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">No blood request records — placeholder for hospital-linked requests</p>
        ) : (
          <div className="space-y-2">
            {issueCerts.map(c => (
              <div key={c.id} className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{c.title}</p>
                <p className="text-xs text-gray-500">{fmtDate(c.issueDate)} — {c.issuerName}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Placeholders */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="card p-4 text-center">
          <Building2 className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-xs font-semibold text-gray-500">Hospital-Linked Blood Request</p>
          <p className="text-[10px] text-gray-400 mt-1">Placeholder — connect to hospital ER for blood requests</p>
        </div>
        <div className="card p-4 text-center">
          <Phone className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-xs font-semibold text-gray-500">Emergency Blood Support</p>
          <p className="text-[10px] text-gray-400 mt-1">Placeholder — emergency blood request through emergency support</p>
        </div>
      </div>

      {shareTarget && (
        <InstitutionShareModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          certificateId={shareTarget.id}
          certificateTitle={shareTarget.title}
        />
      )}
    </div>
  );
}
